import { useEffect, useState } from 'react';
import { api, ApiError } from '../api.ts';
import type { RatingFormData } from '../ratings.ts';

const ERRORS: Record<string, string> = {
  LOW_REASON_REQUIRED: 'Cette note est basse : expliquez en quelques mots ce qui n’a pas convenu (3 caractères minimum).',
  MISSING_CRITERION: 'Notez chaque critère.',
  ALREADY_RATED: 'Vous avez déjà donné votre avis.',
  INVALID_TAGS: 'Un des tags n’est plus proposé : rechargez la page.',
  INVALID_COMMENT: 'Le commentaire est trop long (1 000 caractères maximum).',
};

function lateText(minutes: number): string {
  if (minutes <= 0) return 'arrivée à l’heure';
  if (minutes < 60) return `${minutes} min de retard`;
  if (minutes < 24 * 60) return `${Math.floor(minutes / 60)} h ${minutes % 60} min de retard`;
  return `${Math.floor(minutes / 1440)} j de retard`;
}

function Stars({ value, onChange, label }: { value: number; onChange: (n: number) => void; label: string }) {
  return (
    <div style={{ display: 'flex', gap: 6 }} role="radiogroup" aria-label={label}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" role="radio" aria-checked={value === n} className={`btn btn-sm ${value === n ? 'btn-primary' : 'btn-secondary'}`} style={{ width: 42, padding: '8px 0' }} onClick={() => onChange(n)}>{n}</button>
      ))}
    </div>
  );
}

/**
 * Avis dans les deux sens. Les critères, leur libellé et les tags viennent du Super Admin ; la ponctualité est
 * calculée d'après le pointage et n'est pas modifiable.
 */
export function RatingForm({ missionId, onDone }: { missionId: string; onDone?: () => void }) {
  const [form, setForm] = useState<RatingFormData | null>(null);
  const [scores, setScores] = useState<Record<string, number>>({});
  const [tags, setTags] = useState<Set<string>>(new Set());
  const [comment, setComment] = useState('');
  const [lowReason, setLowReason] = useState('');
  const [forceLow, setForceLow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get<RatingFormData>(`/missions/${missionId}/rating-form`).then((f) => {
      setForm(f);
      setDone(f.alreadyRated);
    }).catch(() => setForm(null));
  }, [missionId]);

  if (!form) return null;
  if (done) {
    return (
      <div className="badge badge-available" style={{ justifyContent: 'center', padding: '10px 14px', lineHeight: 1.5 }}>
        Merci pour votre avis. Il sera visible quand l'autre partie aura aussi noté, ou au plus tard après {form.revealAfterDays} jours.
      </div>
    );
  }

  const manual = form.criteria.filter((c) => !c.auto);
  const given = [...form.criteria.map((c) => (c.auto ? c.auto.score : scores[c.code])).filter((s): s is number => Boolean(s))];
  const average = given.length ? given.reduce((a, b) => a + b, 0) / given.length : null;
  const needsReason = forceLow || (manual.every((c) => scores[c.code]) && average !== null && average < form.lowScoreThreshold);
  const complete = manual.every((c) => scores[c.code]);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/missions/${missionId}/ratings`, {
        criteria: Object.fromEntries(manual.map((c) => [c.code, scores[c.code]])),
        tags: [...tags],
        comment: comment || undefined,
        lowReason: needsReason ? lowReason : undefined,
      });
      setDone(true);
      onDone?.();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : '';
      if (code === 'LOW_REASON_REQUIRED') setForceLow(true);
      setError(ERRORS[code] ?? 'Envoi impossible.');
    } finally {
      setBusy(false);
    }
  }

  const toggle = (code: string) => setTags((prev) => { const next = new Set(prev); if (next.has(code)) next.delete(code); else next.add(code); return next; });
  const chips = (polarity: 'POSITIVE' | 'NEGATIVE') => (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {form.tags.filter((t) => t.polarity === polarity).map((t) => (
        <button key={t.code} type="button" aria-pressed={tags.has(t.code)} className={`badge ${tags.has(t.code) ? (polarity === 'POSITIVE' ? 'badge-available' : 'badge-unavailable') : 'badge-neutral'}`} style={{ border: 'none', cursor: 'pointer', fontSize: 12.5 }} onClick={() => toggle(t.code)}>
          {t.emoji} {t.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ fontWeight: 800 }}>{form.target === 'EXTRA' ? `Noter ${form.ratee.name}` : `Noter ${form.ratee.name}`}</div>
      {error && <div className="error-banner">{error}</div>}

      {form.criteria.map((c) => (
        <div key={c.code} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700 }}>{c.label}</div>
          {c.description && <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{c.description}</div>}
          {c.auto ? (
            <div style={{ fontSize: 13 }}>
              <span className="badge badge-neutral">Calculée d'après le pointage : {c.auto.score}/5</span>{' '}
              <span style={{ color: 'var(--text-tertiary)' }}>({lateText(c.auto.minutesLate)})</span>
            </div>
          ) : (
            <Stars value={scores[c.code] ?? 0} onChange={(n) => setScores((prev) => ({ ...prev, [c.code]: n }))} label={c.label} />
          )}
        </div>
      ))}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700 }}>En un clic</div>
        {chips('POSITIVE')}
        {chips('NEGATIVE')}
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Les tags négatifs sont un retour privé, jamais affichés publiquement.</div>
      </div>

      {needsReason && (
        <div>
          <label className="field-label" htmlFor="low-reason">Raison du mécontentement (obligatoire)</label>
          <textarea id="low-reason" className="input" rows={3} maxLength={500} value={lowReason} onChange={(e) => setLowReason(e.target.value)} />
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>Lue par l'équipe Mon Extra, pas publiée.</div>
        </div>
      )}

      <div>
        <label className="field-label" htmlFor="rating-comment">Commentaire (facultatif)</label>
        <textarea id="rating-comment" className="input" rows={3} maxLength={1000} value={comment} onChange={(e) => setComment(e.target.value)} />
      </div>

      <button className="btn btn-primary" onClick={submit} disabled={busy || !complete || (needsReason && lowReason.trim().length < 3)}>Envoyer mon avis</button>
    </div>
  );
}
